/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';

import { SharedAppConstants } from '@tock/shared-app-constants';
import { core, toJSON, toJSONArray } from '@tock/coretypes';
import { dashboard } from 'types';
import { replacePatronNoteInProfile } from 'utils/noteUtils';
import AppConstants from 'constants/AppConstants';

export interface GuestProfileState {
  profiles: dashboard.GuestProfile[];
  numResults: number;
  profile: dashboard.GuestProfile | null;
  viewProfile: dashboard.GuestProfile | null;
  existingProfile: dashboard.GuestProfile | null;
  isInitialized: boolean;
  isRequestInProgress: boolean;
  error: any | null;
  purchaseHistory: any[];
  walkInHistory: dashboard.VisitSummary[];
  activeBusinesses: core.IBusiness[];
  selectedProfiles: dashboard.GuestProfile[];
  showAddGuestProfileModal: boolean;
  showEditGuestProfileModal: boolean;
  showMergeGuestProfilesModal: boolean;
}

export const initialState: GuestProfileState = {
  profiles: [],
  numResults: 0,
  profile: null,
  viewProfile: null,
  existingProfile: null,
  isInitialized: false,
  isRequestInProgress: false,
  error: null,
  purchaseHistory: [],
  walkInHistory: [],
  activeBusinesses: [],
  selectedProfiles: [],
  showAddGuestProfileModal: false,
  showEditGuestProfileModal: false,
  showMergeGuestProfilesModal: false,
};

export default function reducer(
  state: GuestProfileState = initialState,
  action: any
): GuestProfileState {
  const { context, type, payload } = action;
  switch (type) {
    case SharedAppConstants.BOOKING_MODAL_CLEAR:
      return {
        ...state,
        profile: null,
      };

    case SharedAppConstants.GUEST_PROFILE_FIND_EXISTING_DONE: {
      return {
        ...state,
        existingProfile: toJSON(payload),
        isRequestInProgress: false,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_CLEAR_EXISTING: {
      return {
        ...state,
        existingProfile: null,
        isRequestInProgress: false,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_CLEAR_VIEW: {
      return {
        ...state,
        viewProfile: null,
        isRequestInProgress: false,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_SEARCH_STARTING:
    case SharedAppConstants.GUEST_PROFILE_FIND_EXISTING_STARTING:
    case SharedAppConstants.GUEST_PROFILE_UPDATE_STARTING:
    case SharedAppConstants.GUEST_PROFILE_VIEW_STARTING: {
      return {
        ...state,
        isRequestInProgress: true,
        error: null,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_CLEAR: {
      return {
        ...state,
        profile: null,
        error: null,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_GET_STARTING: {
      return {
        ...state,
        profile: null,
        isRequestInProgress: true,
        error: null,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_SEARCH_FAILED:
    case SharedAppConstants.GUEST_PROFILE_FIND_EXISTING_FAILED:
    case SharedAppConstants.GUEST_PROFILE_MERGE_GUESTS_FAILED:
    case SharedAppConstants.GUEST_PROFILE_FETCH_PATRON_PURCHASE_HISTORY_FAILED: {
      return {
        ...state,
        isRequestInProgress: false,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_GET_FAILED:
    case SharedAppConstants.GUEST_PROFILE_VIEW_FAILED:
      return {
        ...state,
        isRequestInProgress: false,
        error: 'Unable to get the latest information about this guest',
      };
    case SharedAppConstants.GUEST_PROFILE_SEARCH_DONE:
      return {
        ...state,
        profiles: toJSONArray<dashboard.GuestProfile>(payload.guestProfile),
        selectedProfiles:
          state.isInitialized && state.isRequestInProgress ? state.selectedProfiles : [],
        numResults: payload.count,
        isInitialized: true,
        isRequestInProgress: false,
        error: null,
      };
    case SharedAppConstants.GUEST_PROFILE_UPDATE_DONE:
    case SharedAppConstants.GUEST_PROFILE_GET_DONE: {
      return {
        ...state,
        profile: toJSON<dashboard.GuestProfile>(payload),
        isInitialized: true,
        isRequestInProgress: false,
        error: null,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_VIEW_DONE: {
      return {
        ...state,
        viewProfile: toJSON<dashboard.GuestProfile>(payload),
        isInitialized: true,
        isRequestInProgress: false,
        error: null,
      };
    }
    case SharedAppConstants.PATRON_TAG_UPDATE_STARTING:
      return {
        ...state,
        isInitialized: false,
        isRequestInProgress: true,
        error: null,
      };
    case SharedAppConstants.PATRON_TAG_UPDATE_DONE: {
      const { profile } = state;
      // tslint:disable-next-line:prefer-object-spread
      const newProfile = Object.assign({}, profile, {
        tag: payload,
      });
      return {
        ...state,
        profile: newProfile,
        isRequestInProgress: false,
      };
    }
    case SharedAppConstants.PATRON_TAG_UPDATE_FAILED: {
      const errorMessage = 'Unable to update tags';
      return {
        ...state,
        isRequestInProgress: false,
        error: errorMessage,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_UPDATE_FAILED:
      return {
        ...state,
        isRequestInProgress: false,
        error: 'Unable to save changes',
      };
    case AppConstants.HAND_SELL_UPDATE_GUEST_PROFILE_NOTE_DONE:
    case SharedAppConstants.GUEST_PROFILE_NOTE_UPDATE_DONE:
    case SharedAppConstants.GUEST_PROFILE_DIETARY_NOTE_UPDATE_DONE: {
      const { profile } = state;
      const { businessId, type } = context;
      // tslint:disable-next-line:prefer-object-spread
      const newProfile = Object.assign({}, profile);
      replacePatronNoteInProfile(
        newProfile,
        toJSON<dashboard.PatronNote>(payload),
        type,
        businessId
      );
      return {
        ...state,
        profile: newProfile,
        isRequestInProgress: false,
        error: null,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_MERGE_GUESTS_STARTING:
    case SharedAppConstants.GUEST_PROFILE_FETCH_PATRON_PURCHASE_HISTORY_STARTING: {
      return {
        ...state,
        isRequestInProgress: true,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_FETCH_PATRON_PURCHASE_HISTORY_DONE: {
      return {
        ...state,
        purchaseHistory: toJSONArray(payload),
        isRequestInProgress: false,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_MERGE_GUESTS_DONE:
    case SharedAppConstants.GUEST_PROFILE_CLEAR_SELECTED: {
      let selectedProfiles: dashboard.GuestProfile[] = [];

      if (payload) {
        // only remove profiles provided via the payload
        const profileIdsToRemove = toJSONArray<dashboard.GuestProfile>(payload).map(
          (profile) => profile.id
        );
        selectedProfiles = state.selectedProfiles.filter(
          (profile) => !profileIdsToRemove.includes(profile.id)
        );
      }

      return {
        ...state,
        isRequestInProgress: false,
        selectedProfiles,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_SELECT_PROFILE: {
       
      const selectedProfiles: any[] = _.cloneDeep(state.selectedProfiles || []);
      const entry = _.find(selectedProfiles, (profile) => profile.patron.id === payload.patron.id);
      if (entry) {
        return {
          ...state,
        };
      }
      selectedProfiles.push(payload);
      return {
        ...state,
        selectedProfiles,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_UNSELECT_PROFILE: {
       
      let selectedProfiles: any[] = _.cloneDeep(state.selectedProfiles || []);
      const entry = _.find(selectedProfiles, (profile) => profile.patron.id === payload.patron.id);
      if (entry) {
        selectedProfiles = _.without(selectedProfiles, entry);
      }
      return {
        ...state,
        selectedProfiles,
      };
    }

    case SharedAppConstants.GUEST_PROFILE_SELECT_ALL: {
      const selectedProfiles = state.selectedProfiles.concat(state.profiles);
      return {
        ...state,
        selectedProfiles,
      };
    }

    case SharedAppConstants.GUEST_PROFILE_TOGGLE_MERGE_GUEST_PROFILES_MODAL: {
      return {
        ...state,
        showMergeGuestProfilesModal: payload,
      };
    }

    default:
      return state;
  }
}
